import { useState, useEffect, useRef, useCallback } from "react";
import { paymentsService } from "../../../modules/api_client";
import type { CreateIntentDtoCurrencyEnum } from "../../../api";
import { toCheckoutError, type CheckoutError } from "../utils/checkout-error";

/**
 * Fetches a Stripe PaymentIntent client secret for a pre-existing draft SO.
 *
 * Contract: `orderId` is required. The cart-driven checkout creates the
 * draft SO before this hook runs (see PaymentView), and this id rides
 * PaymentIntent metadata so the webhook can confirm the SO atomically on
 * `payment_intent.succeeded`.
 *
 * A refusal (e.g. the order is missing a delivery address) comes back as
 * `error` for the caller to explain inline. Once the buyer has fixed what
 * was refused, `retry()` asks again for the same order and amount.
 */
export function usePaymentIntent(
  amountCents: number,
  currency: CreateIntentDtoCurrencyEnum = "qar",
  orderId: number | null,
) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<CheckoutError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const lastRequestedKeyRef = useRef<string | null>(null);
  const requestKey = `${amountCents}:${currency}:${orderId ?? ""}:${attempt}`;

  useEffect(() => {
    if (amountCents <= 0 || orderId == null) {
      setClientSecret(null);
      setError(null);
      return;
    }

    if (lastRequestedKeyRef.current === requestKey) {
      return;
    }

    lastRequestedKeyRef.current = requestKey;

    setClientSecret(null);
    setIsLoading(true);
    setError(null);

    paymentsService
      .createPaymentIntent({ amountCents, currency, orderId })
      .then((response) => {
        setClientSecret(response.data.clientSecret);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(toCheckoutError(err, "Failed to create payment intent"));
        setIsLoading(false);
      });
  }, [amountCents, currency, orderId, requestKey]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { clientSecret, isLoading, error, retry };
}
